/****************************************************
 * Module Name:  Tender.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Tooltip, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import Constants from "../../../utility/constants";
import renderCellExpand from "../../common/dataGridCellExpand";
import NoPermission from "../../common/noPermission";
import CommonSnackbar from "../../common/commonSnackBar";
import Utility from "../../../utility/utility";
import AddTenderModal from "./addTenderModal";
import TenderProxy from "../../../utility/proxy/manager/tender/tenderProxy";
import dayjs from "dayjs";
import PageTitle from "../../common/pageTitle";


const Tender = () => {
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [tenderListLoader, setTenderListLoader] = useState(false);
    const [tenderList, setTenderList] = useState([]);
    const [access, setAccess] = useState(false);
    const [showAddTenderModal, setShowAddTenderModal] = useState(false);
    const [editOperation, setEditOperation] = useState(false);
    const [sourceData, setSourceData] = useState({});
    const [loadingButton, setLoadingButton] = useState(false);
    const [SuccessSnackbar, setSuccessSnackbar] = useState(false);
    const [tenderOperation, setTenderOperation] = useState('create');
    const [filterValues, setFilterValues] = useState({});

    const local_Permission = Constants.USER_PERMISSION.filter(
        (iPermission) => iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_TENDER);

    useEffect(() => {
        PageTitle("TENDER_OPERATION_TENDER");
        checkPermission();
    }, []);

    const checkPermission = () => {
        if (!Utility.isReadAllowed(local_Permission)) {
            setAccess(true)
            return <NoPermission />
        } else {
            fetchTenderData(true);
        }
    }

    const fetchTenderData = (bAnimateSpinner) => {
        setTenderListLoader(true);
        bAnimateSpinner && setTenderListLoader(true);
        TenderProxy.GetAllTenders(
            (responseData) => {
                if (responseData) {
                    const uniqueFilterValues = {
                        tendername: Utility.getDistinctOptions(responseData, "tendername"),
                        company: Utility.getDistinctOptions(responseData, "companyid"),
                        tendermonth: Utility.getDistinctOptions(responseData, "tendermonth"),
                    };
                    const combinedFilterValues = {
                        company: uniqueFilterValues.company.map(value => {
                            const matchingItem = responseData.find(item => item.companyid === value);
                            return matchingItem && matchingItem.companyname ? `${matchingItem.companyid} | ${matchingItem.companyname}` : '';
                        }),
                        tendername: uniqueFilterValues.tendername,
                        tendermonth: uniqueFilterValues.tendermonth,
                    };
                    setFilterValues(combinedFilterValues);
                    const newResponseData = responseData.map(item => ({
                        ...item,
                        tenderfrom: dayjs(item.tenderfrom.split('T')[0]),
                        tenderto: dayjs(item.tenderto.split('T')[0]),
                    }));
                    setTenderList(newResponseData);
                    setTenderListLoader(false);
                } else {
                    setErrorSnackbar(true);
                    setTenderListLoader(false);
                }
            }
        )
    };

    const initialColumns = () => [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "tendername",
            headerName: "TENDER",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tendername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "companyid",
            headerName: "COMPANY",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.company : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                if (params.row.companyid && params.row.companyname) {
                    return `${params.row.companyid} | ${params.row.companyname}`;
                }
                return "";
            },
            renderCell: (params) => {
                if (params.row.companyid && params.row.companyname) {
                    return `${params.row.companyid} | ${params.row.companyname}`;
                }
                return "";
            },
        },
        {
            field: "tendermonth",
            headerName: "TENDER MONTH",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tendermonth : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "tenderfrom",
            headerName: "TENDER FROM",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: "tenderto",
            headerName: "TENDER TO",
            headerAlign: "center",
            align: "center",
            width: 250,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            width: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setEditOperation(true)
                            setShowAddTenderModal(true)
                            setSourceData(params.row)
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        }

    ]

    const addOrUpdateTenderData = (tenderData) => {
        setTenderListLoader(true);
        setTenderOperation(editOperation ? 'update' : 'create');
        if (editOperation) {
            TenderProxy.UpdateTender(tenderData, (response) => {
                setLoadingButton(false);
                if (response) {
                    fetchTenderData(true);
                    setShowAddTenderModal(false);
                    setTenderListLoader(false);
                    setSuccessSnackbar(true);
                }
                else {
                    setErrorSnackbar(true);
                    setTenderListLoader(false);
                }
            });
        } else {
            TenderProxy.AddTender(tenderData, (response) => {
                setLoadingButton(false);
                if (response) {
                    fetchTenderData(true);
                    setShowAddTenderModal(false);
                    setTenderListLoader(false);
                    setSuccessSnackbar(true);
                }
                else {
                    setErrorSnackbar(true);
                    setTenderListLoader(false);
                }
            });
        }
    }
    const handleClose = () => {
        setSuccessSnackbar(false);
    };

    const handleValidation = (companyData) => {
        //TODO do validation if needed here
        return true;
    };

    const columns = Utility.isUpdateAllowed(local_Permission)
        ? initialColumns()
        : initialColumns().filter((col) => col.field !== "edit");

    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {SuccessSnackbar ? (
                <CommonSnackbar
                    show={SuccessSnackbar}
                    message={`Tender ${tenderOperation === 'update' ? 'updated' : 'created'} successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}

            {showAddTenderModal ? (
                <AddTenderModal
                    Show={true}
                    handleClose={() => setShowAddTenderModal(false)}
                    handleSave={(data) => addOrUpdateTenderData(data)}
                    handleValidation={() => handleValidation()}
                    editOperation={editOperation}
                    sourceData={sourceData}
                    loading={loadingButton}
                />
            ) : null}
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "83vh",
                                "& .lavenderCell": {
                                    color: "#080808",
                                    backgroundColor: "#D8BFD8",
                                },
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                getRowHeight={() => "auto"}
                                density="compact"
                                loading={tenderListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: GridToolbar }}
                                rows={tenderList}
                                columns={columns}
                                getRowId={(row) => row.id}
                                rowSpacingType="border"
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                {tenderListLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            color="primary"
                                            fontSize="medium"
                                            onClick={() => fetchTenderData(true)}
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                                {Utility.isAddAllowed(local_Permission) ? (
                                    <Tooltip title="Add Company">
                                        <Button
                                            color="info"
                                            variant="outlined"
                                            size="small"
                                            sx={{ ml: "5px" }}
                                            onClick={() => {
                                                setShowAddTenderModal(true);
                                                setEditOperation(false);
                                                setSourceData({});
                                            }
                                            }
                                            startIcon={<AddBoxOutlinedIcon size="large" />}
                                        >
                                            Tender
                                        </Button>
                                    </Tooltip>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                )
            }
        </Box>
    )
}
export default Tender;
